import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, CheckCircle, XCircle, Search, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SEOProviderDialog({ open, onOpenChange, client, onSaved }) {
  const [provider, setProvider] = useState('dataforseo');
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadProviders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/app/providers`, { withCredentials: true });
      setProviders(data.providers || []);
    } catch {
      // Non-admin fallback: build a minimal static list so consultants still see the picker
      setProviders([
        { id: 'dataforseo', name: 'DataForSEO', configured: true, short_desc: 'Configuré serveur' },
        { id: 'haloscan', name: 'Haloscan', configured: false, short_desc: 'Clé globale' },
        { id: 'semrush', name: 'SEMrush', configured: false, short_desc: 'Clé globale' },
      ]);
    }
  }, []);

  useEffect(() => {
    if (client) {
      setProvider(client.seo_provider || 'dataforseo');
      setTestResult(null);
      loadProviders();
    }
  }, [client, loadProviders]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API}/clients/${client.id}/seo-provider`,
        { seo_provider: provider },
        { withCredentials: true }
      );
      toast.success('Configuration enregistrée');
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(`Erreur : ${err.response?.data?.detail || `HTTP ${err.response?.status || '?'}`}`);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await axios.put(`${API}/clients/${client.id}/seo-provider`, { seo_provider: provider }, { withCredentials: true });
      const { data } = await axios.post(`${API}/clients/${client.id}/seo-provider/test`, {}, { withCredentials: true });
      setTestResult(data);
      if (data.success) toast.success('Connexion OK');
      else toast.error(data.error || 'Test échoué');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur test';
      setTestResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  if (!client) return null;

  const selected = providers.find(p => p.id === provider);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="seo-provider-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Search className="w-5 h-5 text-[#002FA7]" />
            Provider SEO — {client.name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4 mt-2">
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">Source des données SEO</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {providers.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProvider(p.id)}
                  data-testid={`provider-${p.id}`}
                  className={`text-left border rounded-md p-3 transition-colors ${
                    provider === p.id
                      ? 'border-[#002FA7] bg-[#002FA7]/5 ring-1 ring-[#002FA7]'
                      : 'border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-sm">{p.name}</span>
                    {p.configured ? (
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    ) : (
                      <AlertCircle className="w-3 h-3 text-amber-500" />
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5 line-clamp-2">{p.short_desc}</div>
                </button>
              ))}
            </div>
            {selected && !selected.configured && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                <strong>{selected.name}</strong> n'est pas encore configuré globalement. Demandez à un administrateur de saisir la clé API depuis le panneau "Providers de données SEO" en haut de la page Clients.
              </p>
            )}
          </div>

          {testResult && (
            <div className={`flex items-start gap-2 text-sm p-2 rounded ${
              testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`} data-testid="test-result">
              {testResult.success
                ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                : <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
              <span>{testResult.success ? 'Connexion OK' : (testResult.error || 'Test échoué')}</span>
            </div>
          )}

          <div className="flex justify-between gap-2 pt-2">
            <Button type="button" variant="outline" size="sm"
                    onClick={testConnection} disabled={testing}
                    data-testid="test-connection-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Tester la connexion
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277]"
                      data-testid="seo-provider-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Enregistrer
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
